import { nativeProcessStats } from "@-/dev.improve.haniverse.modules.process-stats";
import delay from "@-/com.npmjs.delay";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import {
  getDiagnosticsRoute,
  isDiagnosticsRecording,
  recordDiagnosticsEvent,
} from "#diagnostics-store";

/** A main-thread read of the graphics cache counters: one hop to the UI thread. */
const MAIN_THREAD_SNAPSHOT_DEADLINE_MS = 10_000;

export function canCaptureGraphicsCacheDiagnostics(): boolean {
  return (
    typeof nativeProcessStats?.getCurrentThreadGraphicsCacheSnapshot === "function" &&
    typeof nativeProcessStats.getMainThreadGraphicsCacheSnapshot === "function"
  );
}

/** One explicit read after returning to the target screen. Never initializes or clears caches. */
export async function captureGraphicsCacheDiagnostics(): Promise<void> {
  if (!isDiagnosticsRecording()) {
    throw new Error("Turn on diagnostic recording before capturing graphics memory.");
  }
  const readCurrent = nativeProcessStats?.getCurrentThreadGraphicsCacheSnapshot;
  const readMain = nativeProcessStats?.getMainThreadGraphicsCacheSnapshot;
  if (readCurrent === undefined || readMain === undefined) {
    throw new Error("Graphics cache diagnostics are unavailable in this build.");
  }
  recordDiagnosticsEvent({
    kind: "marker",
    label: "graphics-cache-capture-scheduled",
    timestamp: Date.now(),
  });
  await delay(5000);
  if (!isDiagnosticsRecording()) {
    throw new Error("Diagnostic recording stopped before graphics memory was captured.");
  }
  const route = getDiagnosticsRoute();
  const currentThread = readCurrent.call(nativeProcessStats);
  const mainThread = await withDeadline(
    {
      deadline: MAIN_THREAD_SNAPSHOT_DEADLINE_MS,
      operation: "main-thread graphics cache snapshot",
    },
    async () => readMain.call(nativeProcessStats),
  );
  const webGPU = nativeProcessStats?.getWebGPUDeviceMemorySnapshot?.() ?? null;
  recordDiagnosticsEvent({
    kind: "marker",
    label: `Graphics cache snapshot: ${JSON.stringify({ currentThread, mainThread, route, webGPU })}`,
    timestamp: Date.now(),
  });
}
